import { Toaster } from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';
import { useTheme } from './ThemeProvider';
import { isFixedTierRole } from './roleTier';
import type { ToastThemeTokens } from './types';

/**
 * Fixed style used for fixed-tier sessions (Super Admin/Platform Admin/Platform Staff) and for
 * pre-login toasts (no user yet, so tier can't be resolved) — matches the pre-ADR-2026-07-D
 * global `<Toaster>` style that lived in main.tsx.
 */
const FIXED_TOAST_THEME: ToastThemeTokens = {
  style: {
    background: '#ffffff',
    color: '#111827',
    borderRadius: '0.75rem',
  },
  success: { iconTheme: { primary: '#16a34a', secondary: '#ffffff' } },
  error: { iconTheme: { primary: '#dc2626', secondary: '#ffffff' } },
};

/**
 * Themed-tier counterpart to the plain `<Toaster>` previously mounted in main.tsx (ADR Section
 * 16, ADR-2026-07-D). Mounted inside `ThemeProvider` so it can read the active theme. Fixed-tier
 * sessions and pre-login toasts always use `FIXED_TOAST_THEME`, regardless of `active_theme`.
 */
export function ThemedToaster() {
  const { user } = useAuthStore();
  const { components } = useTheme();
  const role = user?.roles?.[0];
  const tokens = !role || isFixedTierRole(role) ? FIXED_TOAST_THEME : components.dashboard.toastTheme;

  return (
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 4000,
        style: { ...tokens.style, padding: '0.75rem 1rem', fontSize: '0.875rem' },
        success: tokens.success,
        error: tokens.error,
      }}
    />
  );
}
